import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonItemDivider,
  IonRow,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React, { useEffect, useState } from "react";

import AsyncArtistButton from "../components/AsyncArtistButton";
import Config from "../components/Config";
import Content from "../components/Content";
import GroupAdmin from "../components/GroupAdmin";
import RolesPanel from "../components/RolesPanel";
import Issues from "./Issues";

import { useArtists } from "../data/store/useArtists";
import { useConfig } from "../data/store/useConfig";
import { useProfile } from "../data/store/useProfile";
import { useRoles } from "../data/store/useRoles";
import { useToasts } from "../data/store/useToasts";
import { useAdminPrefs } from "../data/store/useAdminPrefs";

import { Song } from "../models/Song";
import { OwnerId } from "../models/User";
import { usePageTitle } from "../util/sugar";
import { ENABLE_GROUPS } from "../config/env";

import "firebase/compat/firestore";
import AnnouncementsAdmin from "../components/AnnouncementsAdmin";
import TeacherAdmin from "../components/TeacherAdmin";
import TransferOwnership from "../components/TransferOwnership";
import ReindexSearch from "../components/ReindexSearch";
import ArtistPrivacyBackfill from "../components/ArtistPrivacyBackfill";
import GroupPrivacyBackfill from "../components/GroupPrivacyBackfill";

interface OwnerParams {
  ownerId: OwnerId;
}

const NotAuthorized: React.FC<OwnerParams> = ({ ownerId }) => (
  <IonCard>
    <IonCardHeader color="primary">
      <IonCardTitle>
        <IonTitle>Group Status</IonTitle>
      </IonCardTitle>
    </IonCardHeader>

    <IonCardContent>
      <IonItemDivider color="clear" />
      <IonText>Your account is missing administrative authority.</IonText>
      <IonItemDivider color="clear" />
      <IonText>
        If this is an error, please
        <IonButton routerLink={"/issue/group/" + ownerId}>
          {" "}
          Report an Issue
        </IonButton>
      </IonText>
    </IonCardContent>
  </IonCard>
);

// const UsersAdmin: React.FC = () => {
//   const { fetchAll, all } = useArtists();

//   useEffect(() => {
//     fetchAll();
//   }, [all, fetchAll]);

//   return (
//     <IonCard>
//       <IonCardHeader color="primary">
//         <IonCardTitle>
//           <IonTitle>Users</IonTitle>
//         </IonCardTitle>
//       </IonCardHeader>

//       <IonCardContent>
//         {all.map((artistId) => (
//           <AsyncArtistButton variant="alias" key={artistId} id={artistId} />
//         ))}
//         <IonItemDivider />
//         {all.length} Total
//       </IonCardContent>
//     </IonCard>
//   );
// };

const Modify: React.FC = () => {
  // const [songs, setSongs] = useState<Song[]>([]);
  // const { post } = useToasts();
  // const groups = useConfig((x) => x.groups);

  return <Content></Content>;
};

const Admin: React.FC = () => {
  const { ownerId } = useProfile();
  const { role } = useRoles();
  const authority = role && role.authority;
  const isSuperAdmin = authority && authority.includes("super admin");
  const isAdmin = authority && authority.includes("admin");
  const isModerator = authority && authority.includes("moderator");
  const hasDashboard = isSuperAdmin || isAdmin || isModerator;

  const { commentEditMode, missionEditMode, toggleCommentEditMode, toggleMissionEditMode } = useAdminPrefs();

  const [tab, setTab] = useState<
    "issues" | "groups" | "config" | "roles" | "modify"|"assignments"|"transfer"|"reindex"|"announcements"|"artist-privacy-backfill"|"group-privacy-backfill"
  >("issues");
  const [showModify] = useState(false);

  usePageTitle("Admin");

  if (!ownerId || !role) {
    return (
      <Content>
        <IonTitle>
          <IonSpinner name="bubbles" />
        </IonTitle>
      </Content>
    );
  }

  return (
    <Content>
      <IonGrid>
        <IonRow>
          {hasDashboard ? (
            <IonCol size="12" sizeLg="8" offsetLg="2">
              <IonToolbar>
                <IonButton
                  fill="solid"
                  color={tab === "issues" ? "tertiary" : "favorite"}
                  onClick={() => {
                    setTab("issues");
                  }}
                >
                  Issues
                </IonButton>

                {ENABLE_GROUPS && (
                  <IonButton
                    fill="solid"
                    color={tab=='assignments' ? "tertiary" : "favorite"}
                    onClick={() => {
                      setTab('assignments')
                    }}
                  >
                    Assignments
                  </IonButton>
                )}

                {ENABLE_GROUPS && (isSuperAdmin || isAdmin) && (
                  <>
                    <IonButton
                      fill="solid"
                      color={tab === "groups" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("groups");
                      }}
                    >
                      Groups
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "roles" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("roles");
                      }}
                    >
                      Permissions
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "config" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("config");
                      }}
                    >
                      Public Config
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "transfer" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("transfer");
                      }}
                    >
                      Transfer Ownership
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "reindex" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("reindex");
                      }}
                    >
                      Reindex Search
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "artist-privacy-backfill" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("artist-privacy-backfill");
                      }}
                    >
                      Artist Privacy Backfill
                    </IonButton>

                    <IonButton
                      fill="solid"
                      color={tab === "group-privacy-backfill" ? "tertiary" : "favorite"}
                      onClick={() => {
                        setTab("group-privacy-backfill");
                      }}
                    >
                      Group/Feed Backfill
                    </IonButton>
                  </>
                )}

                {(isSuperAdmin || isAdmin) && (
                  <IonButton
                    fill="solid"
                    color={tab === "announcements" ? "tertiary" : "favorite"}
                    onClick={() => {
                      setTab("announcements");
                    }}
                  >
                    Announcements
                  </IonButton>
                )}
              </IonToolbar>
              {(isSuperAdmin || isAdmin) && (
                <IonToolbar>
                  <IonButton
                    fill={commentEditMode ? "solid" : "outline"}
                    color={commentEditMode ? "success" : "medium"}
                    onClick={toggleCommentEditMode}
                  >
                    Edit Comments {commentEditMode ? "On" : "Off"}
                  </IonButton>
                  <IonButton
                    fill={missionEditMode ? "solid" : "outline"}
                    color={missionEditMode ? "success" : "medium"}
                    onClick={toggleMissionEditMode}
                  >
                    Edit Mission {missionEditMode ? "On" : "Off"}
                  </IonButton>
                </IonToolbar>
              )}
              {tab === "groups" && <GroupAdmin />}
              {tab ==='assignments' && <TeacherAdmin />}
              {tab === "issues" && <Issues />}
              {tab === "config" && <Config />}
              {tab === "modify" && <Modify />}
              {tab === "roles" && <RolesPanel />}
              {tab === "transfer" && (isSuperAdmin || isAdmin) && <TransferOwnership />}
              {tab === "reindex" && (isSuperAdmin || isAdmin) && <ReindexSearch />}
              {tab === "artist-privacy-backfill" && (isSuperAdmin || isAdmin) && <ArtistPrivacyBackfill />}
              {tab === "group-privacy-backfill" && (isSuperAdmin || isAdmin) && <GroupPrivacyBackfill />}
              {tab === "announcements" && (isSuperAdmin || isAdmin) && <AnnouncementsAdmin />}
            </IonCol>
          ) : (
            <IonCol size="12" sizeLg="8" offsetLg="2">
              {ownerId ? (
                <NotAuthorized ownerId={ownerId} />
              ) : (
                <IonButton routerLink="/login">Login</IonButton>
              )}
            </IonCol>
          )}
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default Admin;
